<script setup lang="ts">
import {
  Camera,
  Check,
  ChevronRight,
  Eye,
  Mail,
  MapPin,
  Search,
  ShieldCheck,
  Star,
  TrendingUp,
} from "lucide-vue-next";

const { t } = useI18n();

type Role = "clients" | "specialists";
type Step = { title: string; description: string };
type ProfileCard = {
  name: string;
  titleKey: string;
  city: string;
  rating: string;
  reviews: number;
  avatar: string;
};
type RequestCard = {
  name: string;
  titleKey: string;
  city: string;
  metaKey?: string;
  meta?: string;
  timeKey: string;
  time?: string;
  badge: "green" | "gold" | "purple";
  avatar: string;
};

const activeRole = ref<Role>("clients");
const renderedRole = ref<Role>("clients");
const isSwitching = ref(false);

let switchTimer: ReturnType<typeof setTimeout> | null = null;

function selectRole(role: Role) {
  if (role === activeRole.value && role === renderedRole.value) return;

  activeRole.value = role;
  isSwitching.value = true;

  if (switchTimer) clearTimeout(switchTimer);

  switchTimer = setTimeout(async () => {
    renderedRole.value = role;
    await nextTick();

    requestAnimationFrame(() => {
      isSwitching.value = false;
    });
  }, 150);
}

onBeforeUnmount(() => {
  if (switchTimer) clearTimeout(switchTimer);
});

const steps = computed<Step[]>(() => [
  {
    title: t(`how_it_works.${renderedRole.value}.steps.first.title`),
    description: t(
      `how_it_works.${renderedRole.value}.steps.first.description`,
    ),
  },
  {
    title: t(`how_it_works.${renderedRole.value}.steps.second.title`),
    description: t(
      `how_it_works.${renderedRole.value}.steps.second.description`,
    ),
  },
  {
    title: t(`how_it_works.${renderedRole.value}.steps.third.title`),
    description: t(
      `how_it_works.${renderedRole.value}.steps.third.description`,
    ),
  },
]);

const clientProfiles: ProfileCard[] = [
  {
    name: "Tomas Petrauskas",
    titleKey: "how_it_works.mockups.services.plumbing",
    city: "Vilnius",
    rating: "4.9",
    reviews: 128,
    avatar: "/images/home/how-it-works/tomas.webp",
  },
  {
    name: "Ieva Jankauskė",
    titleKey: "how_it_works.mockups.services.plumbing",
    city: "Vilnius",
    rating: "4.8",
    reviews: 96,
    avatar: "/images/home/how-it-works/ieva.webp",
  },
  {
    name: "Mantas Kazlauskas",
    titleKey: "how_it_works.mockups.services.plumbing",
    city: "Vilnius",
    rating: "4.9",
    reviews: 142,
    avatar: "/images/home/how-it-works/mantas.webp",
  },
];

const specialistRequests: RequestCard[] = [
  {
    name: "Agnė",
    titleKey: "how_it_works.mockups.services.leak_repair",
    city: "Vilnius",
    metaKey: "how_it_works.mockups.common.new",
    timeKey: "how_it_works.mockups.common.today_at",
    time: "10:24",
    badge: "green",
    avatar: "/images/home/how-it-works/agne.webp",
  },
  {
    name: "Darius",
    titleKey: "how_it_works.mockups.services.faucet_installation_replacement",
    city: "Kaunas",
    meta: "€60–90",
    timeKey: "how_it_works.mockups.common.today_at",
    time: "09:15",
    badge: "gold",
    avatar: "/images/home/how-it-works/darius.webp",
  },
  {
    name: "Rasa",
    titleKey: "how_it_works.mockups.services.toilet_installation_replacement",
    city: "Klaipėda",
    metaKey: "how_it_works.mockups.common.new",
    timeKey: "how_it_works.mockups.common.yesterday",
    badge: "purple",
    avatar: "/images/home/how-it-works/rasa.webp",
  },
];

function requestBadgeClass(badge: RequestCard["badge"]) {
  if (badge === "green") return "bg-primary/[0.10] text-primary";
  if (badge === "gold") return "bg-primary/[0.07] text-primary";
  return "bg-primary/[0.07] text-primary";
}
</script>

<template>
  <section class="w-full bg-[#F7F9F9] py-12 md:py-16">
    <div class="mx-auto w-full max-w-[1280px] px-4 md:px-6">
      <div
        class="flex flex-col gap-5 md:flex-row md:items-end md:justify-between"
      >
        <h2
          class="font-gabarito text-3xl font-bold leading-none text-app-text md:text-[36px]"
        >
          {{ $t("how_it_works.title") }}
        </h2>

        <div
          class="flex w-fit gap-8"
          role="tablist"
          :aria-label="$t('how_it_works.title')"
        >
          <button
            v-for="role in ['clients', 'specialists'] as Role[]"
            :key="role"
            type="button"
            role="tab"
            :aria-selected="activeRole === role"
            class="relative pb-2.5 text-sm font-semibold transition-colors duration-300 md:text-[15px]"
            :class="
              activeRole === role
                ? 'text-primary after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-primary'
                : 'text-app-muted hover:text-app-text'
            "
            @click="selectRole(role)"
          >
            {{ $t(`how_it_works.tabs.${role}`) }}
          </button>
        </div>
      </div>

      <div class="relative mt-7 md:mt-9 md:min-h-[520px]">
        <div
          class="grid origin-center gap-10 transition-[opacity,transform,filter] duration-300 ease-out md:grid-cols-2 md:gap-6 xl:grid-cols-3"
          :class="
            isSwitching
              ? 'translate-y-2 scale-[0.992] opacity-0 blur-[1.5px]'
              : 'translate-y-0 scale-100 opacity-100 blur-0'
          "
          role="tabpanel"
        >
          <!-- Step 1 -->
          <article class="min-w-0">
            <div
              class="mockup-visual flex min-h-[340px] cursor-default select-none items-center overflow-hidden rounded-[24px] bg-[#ECF3F1] p-[18px] md:h-[388px] md:min-h-0 md:p-6"
            >
              <div
                class="w-full overflow-hidden rounded-[20px] bg-white shadow-[0_14px_34px_rgba(15,23,42,0.05)] ring-1 ring-black/[0.025]"
                :class="renderedRole === 'specialists' ? 'h-full pb-5' : ''"
              >
                <template v-if="renderedRole === 'clients'">
                  <div class="p-5 md:p-6">
                    <div
                      class="flex h-[58px] items-center overflow-hidden rounded-[12px] border border-black/[0.04]"
                    >
                      <div
                        class="flex h-full w-14 items-center justify-center border-r border-black/[0.02] bg-[#F6FAF8] text-primary"
                      >
                        <Search class="h-[18px] w-[18px]" />
                      </div>
                      <div
                        class="flex min-w-0 flex-1 items-center px-4 text-[15px] font-medium text-app-text"
                      >
                        {{ $t("how_it_works.mockups.services.plumbing") }}
                        <ChevronRight
                          class="ml-auto h-4 w-4 rotate-90 text-app-muted"
                        />
                      </div>
                    </div>

                    <div
                      class="mt-3.5 flex h-[58px] items-center overflow-hidden rounded-[12px] border border-black/[0.04]"
                    >
                      <div
                        class="flex h-full w-14 items-center justify-center border-r border-black/[0.02] bg-[#F6FAF8] text-primary"
                      >
                        <MapPin class="h-[18px] w-[18px]" />
                      </div>
                      <div
                        class="flex min-w-0 flex-1 items-center px-4 text-[15px] font-medium text-app-text"
                      >
                        Vilnius
                        <ChevronRight
                          class="ml-auto h-4 w-4 rotate-90 text-app-muted"
                        />
                      </div>
                    </div>

                    <button
                      class="mt-4 flex h-[60px] w-full items-center justify-center rounded-[12px] bg-primary px-4 text-[15px] font-semibold text-white"
                    >
                      {{ $t("how_it_works.mockups.clients.search_action") }}
                    </button>
                  </div>
                </template>

                <template v-else>
                  <div class="flex h-full flex-col p-4 md:p-5">
                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <p class="text-[16px] font-semibold text-app-text">
                          {{
                            $t("how_it_works.mockups.specialists.profile.title")
                          }}
                        </p>
                        <p class="mt-2 text-sm text-app-muted">
                          {{
                            $t(
                              "how_it_works.mockups.specialists.profile.completion",
                            )
                          }}
                        </p>
                        <p
                          class="mt-1 font-gabarito text-[38px] font-bold leading-none text-primary"
                        >
                          80%
                        </p>
                      </div>

                      <div class="relative shrink-0">
                        <img
                          draggable="false"
                          src="/images/home/how-it-works/mantas.webp"
                          alt="Mantas Kazlauskas"
                          class="h-[70px] w-[70px] rounded-full object-cover"
                        />
                        <span
                          class="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-white text-app-muted shadow-sm"
                        >
                          <Camera class="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </div>

                    <div
                      class="mt-3.5 h-2 w-full shrink-0 overflow-hidden rounded-full"
                      style="
                        display: block;
                        min-height: 8px;
                        height: 8px;
                        flex: 0 0 8px;
                        background-color: #f3f5f4;
                      "
                      role="progressbar"
                      aria-valuemin="0"
                      aria-valuemax="100"
                      aria-valuenow="80"
                      :aria-label="
                        $t(
                          'how_it_works.mockups.specialists.profile.completion_aria',
                          { value: 80 },
                        )
                      "
                    >
                      <div
                        class="block h-2 shrink-0 rounded-full"
                        style="
                          display: block;
                          min-height: 8px;
                          height: 8px;
                          width: 80%;
                          background-color: #168774;
                        "
                      ></div>
                    </div>

                    <div
                      class="mt-5 flex flex-1 flex-col divide-y divide-black/[0.018]"
                    >
                      <div
                        class="flex min-h-[58px] flex-1 items-center gap-3 border-t border-black/[0.022] px-0"
                      >
                        <span
                          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/60 text-primary"
                        >
                          <Check class="h-3.5 w-3.5" :stroke-width="2.4" />
                        </span>
                        <div class="min-w-0 flex-1">
                          <p class="truncate text-sm font-medium text-app-text">
                            {{
                              $t(
                                "how_it_works.mockups.specialists.profile.add_services",
                              )
                            }}
                          </p>
                          <p
                            class="mt-0.5 hidden text-[11px] leading-4 text-[#737B84] sm:block"
                          >
                            {{
                              $t(
                                "how_it_works.mockups.specialists.profile.add_services_description",
                              )
                            }}
                          </p>
                        </div>
                        <ChevronRight class="h-4 w-4 shrink-0 text-app-muted" />
                      </div>
                      <div
                        class="flex min-h-[58px] flex-1 items-center gap-3 px-0"
                      >
                        <span
                          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/60 text-primary"
                        >
                          <Check class="h-3.5 w-3.5" :stroke-width="2.4" />
                        </span>
                        <div class="min-w-0 flex-1">
                          <p class="truncate text-sm font-medium text-app-text">
                            {{
                              $t(
                                "how_it_works.mockups.specialists.profile.add_regions",
                              )
                            }}
                          </p>
                          <p
                            class="mt-0.5 hidden text-[11px] leading-4 text-[#737B84] sm:block"
                          >
                            {{
                              $t(
                                "how_it_works.mockups.specialists.profile.add_regions_description",
                              )
                            }}
                          </p>
                        </div>
                        <ChevronRight class="h-4 w-4 shrink-0 text-app-muted" />
                      </div>
                      <div
                        class="flex min-h-[58px] flex-1 items-center gap-3 px-0"
                      >
                        <span
                          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/60 text-primary"
                        >
                          <Check class="h-3.5 w-3.5" :stroke-width="2.4" />
                        </span>
                        <div class="min-w-0 flex-1">
                          <p class="truncate text-sm font-medium text-app-text">
                            {{
                              $t(
                                "how_it_works.mockups.specialists.profile.add_photo",
                              )
                            }}
                          </p>
                          <p
                            class="mt-0.5 hidden text-[11px] leading-4 text-[#737B84] sm:block"
                          >
                            {{
                              $t(
                                "how_it_works.mockups.specialists.profile.add_photo_description",
                              )
                            }}
                          </p>
                        </div>
                        <ChevronRight class="h-4 w-4 shrink-0 text-app-muted" />
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <h3
              class="mt-4 font-gabarito text-[22px] font-bold leading-tight text-app-text md:mt-4"
            >
              {{ steps[0].title }}
            </h3>
            <p
              class="mt-1.5 max-w-[96%] text-[15px] leading-[1.6] text-[#5C6570] md:mt-1"
            >
              {{ steps[0].description }}
            </p>
          </article>

          <!-- Step 2 -->
          <article class="min-w-0">
            <div
              class="mockup-visual flex min-h-[340px] cursor-default select-none items-center overflow-hidden rounded-[24px] bg-[#ECF3F1] p-[18px] md:h-[388px] md:min-h-0 md:p-6"
            >
              <template v-if="renderedRole === 'clients'">
                <div class="flex h-full w-full flex-col justify-center gap-3">
                  <div
                    v-for="profile in clientProfiles"
                    :key="profile.name"
                    class="min-h-[88px] rounded-[17px] bg-white px-3.5 py-3 shadow-[0_10px_26px_rgba(15,23,42,0.04)] ring-1 ring-black/[0.025] md:h-[96px] md:px-4"
                  >
                    <div class="flex h-full items-center gap-3">
                      <img
                        draggable="false"
                        :src="profile.avatar"
                        :alt="profile.name"
                        class="h-[52px] w-[52px] shrink-0 rounded-full object-cover md:h-14 md:w-14"
                      />
                      <div class="min-w-0 flex-1">
                        <p
                          class="truncate text-[14px] font-semibold leading-5 text-app-text"
                        >
                          {{ profile.name }}
                        </p>
                        <p
                          class="truncate text-[13px] leading-5 text-app-muted"
                        >
                          {{ $t(profile.titleKey) }}
                        </p>
                        <p
                          class="flex items-center gap-1 text-[11px] leading-4 text-app-muted"
                        >
                          <MapPin class="h-3 w-3" /> {{ profile.city }}
                        </p>
                      </div>
                      <div
                        class="flex h-full shrink-0 flex-col items-end justify-center text-right"
                      >
                        <p
                          class="flex items-center justify-end gap-1 text-[13px] font-semibold leading-none text-app-text"
                        >
                          <Star class="h-3.5 w-3.5 fill-current text-primary" />
                          {{ profile.rating }}
                        </p>
                        <p class="mt-2 text-[11px] leading-none text-app-muted">
                          ({{ profile.reviews }})
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else>
                <div
                  class="flex h-full w-full flex-col overflow-hidden rounded-[20px] bg-white shadow-[0_14px_34px_rgba(15,23,42,0.05)] ring-1 ring-black/[0.025]"
                >
                  <div
                    class="flex h-[52px] items-center justify-between border-b border-black/[0.025] px-4"
                  >
                    <p
                      class="text-[15px] font-semibold tracking-[-0.01em] text-app-text"
                    >
                      {{
                        $t("how_it_works.mockups.specialists.requests.title")
                      }}
                    </p>
                    <button
                      class="inline-flex items-center gap-1 text-[11px] font-semibold text-primary"
                    >
                      {{
                        $t("how_it_works.mockups.specialists.requests.view_all")
                      }}
                      <ChevronRight class="h-3.5 w-3.5" :stroke-width="1.8" />
                    </button>
                  </div>

                  <div
                    class="flex flex-1 flex-col divide-y divide-black/[0.02]"
                  >
                    <div
                      v-for="request in specialistRequests"
                      :key="request.name"
                      class="flex min-h-[70px] flex-1 items-center gap-3 px-4"
                    >
                      <img
                        draggable="false"
                        :src="request.avatar"
                        :alt="request.name"
                        class="h-[42px] w-[42px] shrink-0 rounded-full object-cover ring-1 ring-black/[0.03]"
                      />
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-1.5">
                          <p
                            class="truncate text-[13px] font-semibold text-app-text"
                          >
                            {{ request.name }}
                          </p>
                          <span
                            class="h-[5px] w-[5px] rounded-full bg-primary/90"
                          />
                        </div>
                        <p class="truncate text-[11px] text-app-muted">
                          {{ $t(request.titleKey) }}
                        </p>
                        <p
                          class="mt-0.5 hidden items-center gap-1 text-[10px] leading-4 text-[#737B84] md:flex"
                        >
                          <MapPin class="h-3 w-3" />{{ request.city }}
                        </p>
                      </div>
                      <div class="shrink-0 text-right">
                        <span
                          class="inline-flex rounded-full px-2.5 py-1 text-[10.5px] font-semibold leading-none"
                          :class="requestBadgeClass(request.badge)"
                          >{{
                            request.metaKey ? $t(request.metaKey) : request.meta
                          }}</span
                        >
                        <p
                          class="mt-1.5 text-[9.5px] leading-none text-app-muted"
                        >
                          {{
                            request.timeKey ===
                            "how_it_works.mockups.common.today_at"
                              ? $t(request.timeKey, { time: request.time })
                              : $t(request.timeKey)
                          }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    class="flex h-[48px] shrink-0 items-center gap-2.5 border-t border-black/[0.025] bg-[#FCFDFD] px-4 text-[10.5px] text-app-muted"
                  >
                    <span
                      class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/[0.055] text-primary"
                    >
                      <Mail class="h-3.5 w-3.5" :stroke-width="1.8" />
                    </span>
                    <span>{{
                      $t("how_it_works.mockups.specialists.requests.footer")
                    }}</span>
                  </div>
                </div>
              </template>
            </div>

            <h3
              class="mt-4 font-gabarito text-[22px] font-bold leading-tight text-app-text md:mt-4"
            >
              {{ steps[1].title }}
            </h3>
            <p
              class="mt-1.5 max-w-[96%] text-[15px] leading-[1.6] text-[#5C6570] md:mt-1"
            >
              {{ steps[1].description }}
            </p>
          </article>

          <!-- Step 3 -->
          <article class="min-w-0">
            <div
              class="mockup-visual flex min-h-[340px] cursor-default select-none items-center overflow-hidden rounded-[24px] bg-[#ECF3F1] p-[18px] md:h-[388px] md:min-h-0 md:p-6"
            >
              <div
                class="h-full w-full overflow-hidden rounded-[20px] bg-white p-4 shadow-[0_16px_40px_rgba(15,23,42,0.05)] ring-1 ring-black/[0.025] md:p-5"
              >
                <template v-if="renderedRole === 'clients'">
                  <div class="flex h-full flex-col">
                    <div
                      class="flex items-start gap-3 border-b border-black/[0.02] pb-3"
                    >
                      <img
                        draggable="false"
                        src="/images/home/how-it-works/tomas.webp"
                        alt="Tomas Petrauskas"
                        class="h-[52px] w-[52px] shrink-0 rounded-full object-cover md:h-14 md:w-14"
                      />
                      <div class="min-w-0 flex-1">
                        <div class="flex items-start justify-between gap-2">
                          <div class="min-w-0">
                            <p
                              class="truncate text-[14px] font-semibold text-app-text"
                            >
                              Tomas Petrauskas
                            </p>
                            <p class="text-[13px] text-app-muted">
                              {{
                                $t("how_it_works.mockups.services.pipe_repair")
                              }}
                            </p>
                            <p
                              class="mt-0.5 flex items-center gap-1 text-[11px] text-app-muted"
                            >
                              <MapPin class="h-3 w-3" />Vilnius
                            </p>
                          </div>
                          <div class="self-center shrink-0 text-right">
                            <p
                              class="flex items-center justify-end gap-1 text-[13px] font-semibold leading-none text-app-text"
                            >
                              <Star
                                class="h-3.5 w-3.5 fill-current text-primary"
                              />4.9
                            </p>
                            <p class="hidden text-app-muted">(128)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="mt-3 rounded-[12px] bg-[#F0F7F4] px-3.5 py-2.5">
                      <div class="flex items-center gap-2.5">
                        <ShieldCheck class="h-4 w-4 shrink-0 text-primary" />
                        <div>
                          <p class="text-[13px] font-semibold text-app-text">
                            {{
                              $t("how_it_works.mockups.clients.from_price", {
                                price: "€45",
                              })
                            }}
                          </p>
                          <p class="text-[11px] text-app-muted">
                            {{ $t("how_it_works.mockups.clients.price_note") }}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      class="mb-5 mt-3 mr-auto w-[72%] rounded-[12px] rounded-bl-sm bg-[#FAFAFB] px-3.5 py-2.5 text-left text-[12px] leading-[17px] text-app-text sm:w-[70%] md:w-[68%]"
                    >
                      {{ $t("how_it_works.mockups.clients.message.line1") }}
                      {{ $t("how_it_works.mockups.clients.message.line2") }}
                      <p class="mt-0.5 text-left text-[10px] text-app-muted">
                        10:24
                      </p>
                    </div>

                    <div class="mt-auto border-t border-black/[0.02] pt-3.5">
                      <div class="flex items-center justify-between gap-2">
                        <div
                          class="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary/[0.055] px-2.5 py-1.5 text-[10px] font-medium leading-none text-app-muted sm:text-[10.5px]"
                        >
                          <ShieldCheck
                            class="h-3.5 w-3.5 shrink-0 text-primary"
                            :stroke-width="1.8"
                          />
                          <span>{{
                            $t(
                              "how_it_works.mockups.clients.background_checked",
                            )
                          }}</span>
                        </div>

                        <div
                          class="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-[#FFF8E8] px-2.5 py-1.5 text-[#DEA018]"
                          aria-label="Client rating stars"
                        >
                          <Star
                            v-for="i in 5"
                            :key="`client-rating-star-${i}`"
                            class="h-3.5 w-3.5 fill-current"
                            :stroke-width="1.6"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="box-border flex h-full flex-col">
                    <div
                      class="flex items-start justify-between gap-3 border-b border-black/[0.02] pb-3"
                    >
                      <div class="flex min-w-0 items-start gap-3">
                        <img
                          draggable="false"
                          src="/images/home/how-it-works/mantas.webp"
                          alt="Mantas Kazlauskas"
                          class="h-[52px] w-[52px] shrink-0 rounded-full object-cover md:h-14 md:w-14"
                        />
                        <div class="min-w-0">
                          <p
                            class="truncate text-[14px] font-semibold text-app-text"
                          >
                            Mantas Kazlauskas
                          </p>
                          <p class="text-[13px] text-app-muted">
                            {{ $t("how_it_works.mockups.services.plumber") }}
                          </p>
                          <p
                            class="mt-0.5 flex items-center gap-1 text-[11px] text-app-muted"
                          >
                            <MapPin class="h-3 w-3" />Vilnius
                          </p>
                        </div>
                      </div>
                      <span
                        class="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#EEF8F3] px-2.5 py-1.5 text-[9.5px] font-semibold leading-none text-primary"
                      >
                        <ShieldCheck class="h-3 w-3" :stroke-width="1.8" />
                        {{
                          $t("how_it_works.mockups.specialists.grow.verified")
                        }}
                      </span>
                    </div>

                    <div class="mt-3 grid grid-cols-2 gap-2.5">
                      <div
                        class="rounded-[14px] border border-black/[0.045] bg-[#FCFDFD] p-3"
                      >
                        <div class="flex items-center gap-2.5">
                          <span
                            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FFF8E8] text-[#D99A13]"
                          >
                            <Star
                              class="h-4 w-4 fill-current"
                              :stroke-width="1.6"
                            />
                          </span>
                          <p
                            class="font-gabarito text-[24px] font-bold leading-none tracking-[-0.02em] text-app-text"
                          >
                            4.9
                          </p>
                        </div>
                        <p class="mt-2 text-[11px] text-app-muted">
                          {{
                            $t(
                              "how_it_works.mockups.specialists.grow.average_rating",
                            )
                          }}
                        </p>
                        <div class="mt-2 flex gap-px text-primary">
                          <Star
                            v-for="i in 5"
                            :key="i"
                            class="h-[11px] w-[11px] fill-current"
                            :stroke-width="1.5"
                          />
                        </div>
                        <p class="mt-1.5 text-[9.5px] text-app-muted">
                          {{
                            $t("how_it_works.mockups.common.reviews", {
                              count: 12,
                            })
                          }}
                        </p>
                      </div>

                      <div
                        class="rounded-[14px] border border-black/[0.045] bg-[#FCFDFD] p-3"
                      >
                        <div class="flex items-center gap-2.5">
                          <span
                            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-black/[0.025] text-app-muted"
                          >
                            <Eye class="h-4 w-4" :stroke-width="1.8" />
                          </span>
                          <p
                            class="font-gabarito text-[24px] font-bold leading-none tracking-[-0.02em] text-app-text"
                          >
                            356
                          </p>
                        </div>
                        <p class="mt-2 text-[11px] text-app-muted">
                          {{
                            $t(
                              "how_it_works.mockups.specialists.grow.profile_views",
                            )
                          }}
                        </p>
                        <svg
                          class="mt-2 h-8 w-full overflow-visible"
                          viewBox="0 0 110 34"
                          fill="none"
                          aria-hidden="true"
                        >
                          <defs>
                            <linearGradient
                              id="profileViewsArea"
                              x1="0"
                              y1="7"
                              x2="0"
                              y2="34"
                              gradientUnits="userSpaceOnUse"
                            >
                              <stop stop-color="#168774" stop-opacity="0.22" />
                              <stop
                                offset="1"
                                stop-color="#168774"
                                stop-opacity="0"
                              />
                            </linearGradient>
                          </defs>
                          <path
                            d="M4 27C16 27 16 14 28 14C40 14 41 26 54 26C67 26 68 16 80 16C92 16 94 9 106 8L106 34L4 34Z"
                            fill="url(#profileViewsArea)"
                          />
                          <path
                            d="M4 27C16 27 16 14 28 14C40 14 41 26 54 26C67 26 68 16 80 16C92 16 94 9 106 8"
                            stroke="currentColor"
                            stroke-width="2.4"
                            stroke-linecap="round"
                            class="text-primary"
                          />
                        </svg>
                        <p
                          class="mt-1.5 inline-flex rounded-full bg-primary/[0.055] px-2 py-1 text-[9.5px] font-semibold leading-none text-primary"
                        >
                          {{
                            $t(
                              "how_it_works.mockups.specialists.grow.week_change",
                              { value: 28 },
                            )
                          }}
                        </p>
                      </div>
                    </div>

                    <div class="mt-auto shrink-0 pt-4 pb-1">
                      <div
                        class="flex items-center gap-2.5 rounded-[14px] border border-primary/[0.08] bg-primary/[0.055] px-3 py-2.5"
                      >
                        <span
                          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-primary ring-1 ring-primary/[0.06]"
                        >
                          <TrendingUp class="h-4 w-4" :stroke-width="1.8" />
                        </span>
                        <div class="min-w-0 flex-1">
                          <p
                            class="truncate text-[11.5px] font-semibold text-app-text"
                          >
                            {{
                              $t(
                                "how_it_works.mockups.specialists.grow.more_views",
                              )
                            }}
                          </p>
                          <p
                            class="mt-0.5 truncate text-[9.5px] text-app-muted"
                          >
                            {{
                              $t(
                                "how_it_works.mockups.specialists.grow.keep_updated",
                              )
                            }}
                          </p>
                        </div>
                        <ChevronRight
                          class="h-4 w-4 shrink-0 text-primary/70"
                          :stroke-width="1.8"
                        />
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <h3
              class="mt-4 font-gabarito text-[22px] font-bold leading-tight text-app-text md:mt-4"
            >
              {{ steps[2].title }}
            </h3>
            <p
              class="mt-1.5 max-w-[96%] text-[15px] leading-[1.6] text-[#5C6570] md:mt-1"
            >
              {{ steps[2].description }}
            </p>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.mockup-visual,
.mockup-visual * {
  user-select: none;
  -webkit-user-select: none;
}

.mockup-visual img {
  -webkit-user-drag: none;
}
</style>
